import type { SourceInfo } from "#/types";

import {
  exportSource as apiExportSource,
  importSource as apiImportSource,
  deleteSource,
  getSources,
  toggleSourceDisabled,
} from "#/client";
import { useEffect, useState } from "react";

export function useSources(isAdmin: boolean) {
  const [sources, setSources] = useState<SourceInfo[]>([]);
  const [loadingState, setLoadingState] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [importJson, setImportJson] = useState("");

  const fetchSources = async () => {
    try {
      const data = await getSources();
      setSources(data);
    } catch (error) {
      console.error("Failed to fetch sources:", error);
    } finally {
      setLoadingState(false);
    }
  };

  useEffect(() => {
    void fetchSources();
  }, [isAdmin]);

  const exportSource = async (id: string) => {
    try {
      const jsonStr = await apiExportSource(id);
      const JSON_SPACING = 2;
      const blob = new Blob([JSON.stringify(jsonStr, undefined, JSON_SPACING)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${id}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleImport = async () => {
    if (!importJson) {
      return;
    }

    try {
      await apiImportSource(importJson);
      setImportJson("");
      setShowImport(false);
      void fetchSources();
    } catch (error) {
      console.error("Import failed:", error);
      alert(`Import failed: ${String(error)}`);
    }
  };

  const toggleDisabled = async (id: string, currentDisabled: boolean) => {
    try {
      await toggleSourceDisabled(id);
      void fetchSources();
    } catch (error) {
      console.error("Failed to toggle source disabled status:", error);
      alert(`Failed to update: ${String(error)}`);
    }
  };

  const handleDelete = async (id: string) => {
    if (
      !confirm(
        `Are you sure you want to delete source "${id}"? This will also delete all associated books and chapters.`,
      )
    ) {
      return;
    }
    try {
      await deleteSource(id);
      void fetchSources();
    } catch (error) {
      console.error("Failed to delete source:", error);
      alert(`Failed to delete: ${String(error)}`);
    }
  };

  return {
    sources,
    loadingState,
    showImport,
    importJson,
    setShowImport,
    setImportJson,
    exportSource,
    handleImport,
    fetchSources,
    toggleDisabled,
    handleDelete,
  };
}
